import Cookies from 'universal-cookie';
import Config from '../services/config/config';

type TokenName = 'accessToken' | 'refreshToken';

class AuthUtils {
  static readonly tokenNames: Record<TokenName, TokenName> = {
    accessToken: 'accessToken',
    refreshToken: 'refreshToken',
  };

  static cookieConfig = (): CookieConfig => {
    const cookieOptions: CookieConfig = {
      options: {
        sameSite: 'none',
      },
    };
    if (Config.ENV === 'production' || Config.ENV === 'staging') {
      cookieOptions.options = {
        ...cookieOptions.options,
        secure: true,
        path: '/authdata',
        domain: Config.URL,
      };
    }
    return cookieOptions;
  };

  static isTokenFromCookieExpired = (
    tokenBaseName: TokenName,
    cookies: Cookies = new Cookies()
  ) => {
    try {
      const expires = cookies.get(`${tokenBaseName}_expires`) || null;
      return Date.now() > Number(expires);
    } catch (e) {
      return true;
    }
  };

  static getAccessToken = () => {
    return AuthUtils.getValidTokenFromCookieByName(AuthUtils.tokenNames.accessToken) || null;
  };

  static getRefreshToken = () => {
    return AuthUtils.getValidTokenFromCookieByName(AuthUtils.tokenNames.refreshToken) || null;
  };

  static getSessionExpirationDate = (): Date | null => {
    const cookies = new Cookies();
    const expirationDate = cookies.get(`${AuthUtils.tokenNames.refreshToken}_expires`);

    return expirationDate ? new Date(Number(expirationDate)) : null;
  };

  static clearTokens = () => {
    // Remove these calls after a while. They're being left to remove any info from the previous cross store solution.
    localStorage.removeItem(AuthUtils.tokenNames.accessToken);
    localStorage.removeItem(`${AuthUtils.tokenNames.accessToken}_expires`);
    localStorage.removeItem(AuthUtils.tokenNames.refreshToken);
    localStorage.removeItem(`${AuthUtils.tokenNames.refreshToken}_expires`);
  };

  static getCookieByKey = (key: string) => {
    const cookies = new Cookies();
    return cookies.get(key);
  };

  static isCrossStorePossible = () => {
    const cookies = new Cookies();
    cookies.set('crossStoreTest', 'test', AuthUtils.cookieConfig().options);

    return cookies.get('crossStoreTest') === 'test';
  };

  private static getValidTokenFromCookieByName = (tokenName: TokenName) => {
    const cookies = new Cookies();
    if (AuthUtils.isTokenFromCookieExpired(tokenName, cookies)) return null;

    return cookies.get(tokenName) || null;
  };
}

export default AuthUtils;
